import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import "./DynamicHotspot.css";

const DynamicHotspot = ({ targetId, title, description, offsetX = 30, offsetY = 0, tooltipPosition = "right" }) => {
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const targetElement = document.getElementById(targetId);
        if (!targetElement) {
            console.warn(`[Hotspot: ${title}] Target '${targetId}' not found`);
            return;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                setIsVisible(entries[0].isIntersecting);
            },
            { root: null, threshold: 0.1 }
        );

        observer.observe(targetElement);

        // ✅ Force an immediate check on load
        const rect = targetElement.getBoundingClientRect();
        setIsVisible(rect.top >= 0 && rect.bottom <= window.innerHeight);

        return () => observer.disconnect();
    }, [targetId]);

    useEffect(() => {
        if (!isVisible) return;

        const targetElement = document.getElementById(targetId);
        if (targetElement) {
            const updatePosition = () => {
                const rect = targetElement.getBoundingClientRect();
                setPosition({ x: -121+ offsetX, y: rect.top + window.scrollY + rect.height / 2 + offsetY });
            };

            updatePosition();
            const handleScroll = () => requestAnimationFrame(updatePosition);

            window.addEventListener("scroll", handleScroll);
            window.addEventListener("resize", updatePosition);

            return () => {
                window.removeEventListener("scroll", handleScroll);
                window.removeEventListener("resize", updatePosition);
            };
        }
    }, [isVisible, targetId, offsetX, offsetY]);

    return (
        <AnimatePresence>
            {isVisible && (
                <motion.div
                    className={`dynamic-hotspot tooltip-${tooltipPosition}`}
                    style={{ left: `${position.x}px`, top: `${position.y}px` }}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={{ duration: 0.2 }}
                >
                    <div className="hotspot-dot"></div>
                    <div className="hotspot-tooltip">
                        <strong>{title}</strong>
                        <p className="hotspot-body">{description}</p>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default DynamicHotspot;
